import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import CookieOutlinedIcon from "@mui/icons-material/CookieOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CodeIcon from "@mui/icons-material/Code";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import EmojiSymbolsOutlinedIcon from "@mui/icons-material/EmojiSymbolsOutlined";
import FormatPaintOutlinedIcon from "@mui/icons-material/FormatPaintOutlined";
import WebAssetOutlinedIcon from "@mui/icons-material/WebAssetOutlined";
import BrandingWatermarkOutlinedIcon from "@mui/icons-material/BrandingWatermarkOutlined";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { api } from "../api/axios";
import { PageHeader, Pill, SectionCard } from "../components/ui";
import {
  WIDGET_ICON_OPTIONS,
  WIDGET_POSITION_OPTIONS,
  WidgetIcon,
  normalizeWidgetPosition,
} from "../widgetIcons";

const TABS = [
  { value: "general", label: "כללי", icon: <TuneRoundedIcon /> },
  { value: "design", label: "עיצוב הווידג׳ט", icon: <PaletteOutlinedIcon /> },
  { value: "behavior", label: "התנהגות", icon: <TouchAppOutlinedIcon /> },
  { value: "privacy", label: "פרטיות ועוגיות", icon: <CookieOutlinedIcon /> },
  { value: "statement", label: "הצהרת נגישות", icon: <DescriptionOutlinedIcon /> },
];

function safeHex(v) {
  const s = String(v || "").trim();
  if (/^#[0-9a-f]{6}$/i.test(s)) return s;
  if (/^#[0-9a-f]{3}$/i.test(s)) return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
  return "#000000";
}

/** Hex text input with an inline native color-picker swatch. */
function ColorField({ label, value, onChange, placeholder, helperText }) {
  return (
    <TextField
      label={label}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      helperText={helperText}
      fullWidth
      inputProps={{ dir: "ltr", spellCheck: false }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Box
              sx={{
                position: "relative",
                width: 28,
                height: 28,
                borderRadius: 1,
                border: 1,
                borderColor: (t) => t.aw.borderStrong,
                bgcolor: safeHex(value),
                overflow: "hidden",
                "&:focus-within": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
              }}
            >
              <input
                type="color"
                value={safeHex(value)}
                onChange={(e) => onChange(e.target.value)}
                aria-label={`${label} – בחירת צבע`}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer", border: 0, padding: 0 }}
              />
            </Box>
          </InputAdornment>
        ),
      }}
    />
  );
}

/** Label + description + switch row (replaces yes/no selects). */
function SwitchRow({ label, description, checked, onChange, disabled }) {
  const id = useId();
  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="label" htmlFor={id} sx={{ fontWeight: 600, fontSize: "0.92rem", display: "block", cursor: "pointer" }}>
          {label}
        </Typography>
        {description ? (
          <Typography variant="body2" id={`${id}-d`} sx={{ color: "text.secondary" }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      <Switch
        id={id}
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        sx={{
          "& .MuiSwitch-thumb": { boxShadow: "0 1px 3px rgba(15,23,42,0.3)" },
          "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": { bgcolor: "#fff" },
        }}
        inputProps={description ? { "aria-describedby": `${id}-d` } : undefined}
      />
    </Stack>
  );
}

function SubHeading({ icon, children, sx }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, ...sx }}>
      {icon ? <Box sx={{ color: (t) => t.aw.faint, display: "flex", "& svg": { fontSize: 18 } }}>{icon}</Box> : null}
      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
        {children}
      </Typography>
    </Stack>
  );
}

const pxAdornment = { endAdornment: <InputAdornment position="end">px</InputAdornment> };
const grid2 = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 };
const grid3 = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 2 };

// Upload endpoint may return a relative URL; the widget runs on customer domains so it needs an absolute one.
function toAbsoluteUrl(u) {
  const s = String(u || "").trim();
  if (!s || /^https?:\/\//i.test(s)) return s;
  const runtime = window.__AW_RUNTIME_CONFIG__ || {};
  const base = String(runtime.widgetServerUrl || runtime.apiBaseUrl || window.location.origin).replace(/\/$/, "");
  return `${base}${s.startsWith("/") ? "" : "/"}${s}`;
}

export default function SiteEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const iconFileRef = useRef(null);
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("lg"));
  const [searchParams, setSearchParams] = useSearchParams();
  const [site, setSite] = useState(null);
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [status, setStatus] = useState("active");
  const [brandName, setBrandName] = useState("");
  const [brandLogoUrl, setBrandLogoUrl] = useState("");
  const [brandMarketingText, setBrandMarketingText] = useState("");
  const [widgetFontFamily, setWidgetFontFamily] = useState("");
  const [widgetPosition, setWidgetPosition] = useState("bottom-right");
  const [widgetMarginSide, setWidgetMarginSide] = useState(20);
  const [widgetButtonSize, setWidgetButtonSize] = useState(52);
  const [widgetButtonShape, setWidgetButtonShape] = useState("circle");
  const [widgetIconUrl, setWidgetIconUrl] = useState("");
  const [widgetIconColor, setWidgetIconColor] = useState("#ffffff");
  const [widgetLanguage, setWidgetLanguage] = useState("auto");
  const [cookieButtonMode, setCookieButtonMode] = useState("panel");
  const [widgetShortcut, setWidgetShortcut] = useState(true);
  const [widgetAllowHide, setWidgetAllowHide] = useState(true);
  const [widgetMarginTop, setWidgetMarginTop] = useState(20);
  const [widgetMarginBottom, setWidgetMarginBottom] = useState(20);
  const [privacyMarginTop, setPrivacyMarginTop] = useState(20);
  const [privacyMarginBottom, setPrivacyMarginBottom] = useState(20);
  const [widgetIconStyle, setWidgetIconStyle] = useState("default");
  const [widgetButtonColor, setWidgetButtonColor] = useState("#1976d2");
  const [widgetButtonHoverColor, setWidgetButtonHoverColor] = useState("#1565c0");
  const [widgetShowCloseButton, setWidgetShowCloseButton] = useState(true);
  const [widgetCustomTitle, setWidgetCustomTitle] = useState("");
  const [widgetCustomTagline, setWidgetCustomTagline] = useState("");
  const [widgetSizeScale, setWidgetSizeScale] = useState(100);
  const [privacyEnabled, setPrivacyEnabled] = useState(false);
  const [privacyConfig, setPrivacyConfig] = useState({
    position: 'left',
    bannerTitle: '',
    bannerText: '',
    acceptAllText: '',
    rejectText: '',
    preferencesText: '',
    modalTitle: '',
    savePrefsText: '',
    policyUrl: '',
    policyText: '',
    showReminder: true,
    bannerBg: '#ffffff',
    bannerTextColor: '#111111',
    primaryBtnBg: '#1976d2',
    primaryBtnTextColor: '#ffffff',
    secondaryBtnBg: '#f0f0f0',
    secondaryBtnTextColor: '#333333',
    linkColor: '#1976d2',
    bannerRadius: 12,
    bannerShadow: '0 -2px 16px rgba(0,0,0,0.12)',
    reminderBg: '#1976d2',
    reminderTextColor: '#ffffff',
    reminderPosition: 'right',
    reminderBottom: 20,
  });
  const [privacyStats, setPrivacyStats] = useState(null);
  const [customStatementUrl, setCustomStatementUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const preview = useMemo(
    () => ({
      name: (brandName || "").trim() || "Accss",
      logoUrl: (brandLogoUrl || "").trim(),
      marketingText: (brandMarketingText || "").trim(),
    }),
    [brandName, brandLogoUrl, brandMarketingText]
  );

  useEffect(() => {
    api
      .get("/api/sites")
      .then(({ data }) => {
        const s = (data.sites || []).find((x) => String(x.id) === String(id));
        if (!s) throw new Error("not_found");
        setSite(s);
        setName(s.name);
        setDomain(s.domain);
        setStatus(s.status);
        setBrandName(s.brand_name || "");
        setBrandLogoUrl(s.brand_logo_url || "");
        setBrandMarketingText(s.brand_marketing_text || "");
        setWidgetFontFamily(s.widget_font_family || "");
        setWidgetPosition(normalizeWidgetPosition(s.widget_position));
        setWidgetMarginSide(s.widget_margin_side ?? 20);
        setWidgetButtonSize(s.widget_button_size ?? 52);
        setWidgetButtonShape(s.widget_button_shape || "circle");
        setWidgetIconUrl(s.widget_icon_url || "");
        setWidgetIconColor(s.widget_icon_color || "#ffffff");
        setWidgetLanguage(s.widget_language || "auto");
        setCookieButtonMode(s.cookie_button_mode || "panel");
        setWidgetShortcut(s.widget_shortcut === undefined || s.widget_shortcut === null ? true : !!Number(s.widget_shortcut));
        setWidgetAllowHide(s.widget_allow_hide === undefined || s.widget_allow_hide === null ? true : !!Number(s.widget_allow_hide));
        setWidgetMarginTop(s.widget_margin_top ?? 20);
        setWidgetMarginBottom(s.widget_margin_bottom ?? 20);
        setPrivacyMarginTop(s.privacy_margin_top ?? 20);
        setPrivacyMarginBottom(s.privacy_margin_bottom ?? 20);
        setWidgetIconStyle(s.widget_icon_style || "default");
        setWidgetButtonColor(s.widget_button_color || "#1976d2");
        setWidgetButtonHoverColor(s.widget_button_hover_color || "#1565c0");
        setWidgetShowCloseButton(!!s.widget_show_close_button);
        setWidgetCustomTitle(s.widget_custom_title || "");
        setWidgetCustomTagline(s.widget_custom_tagline || "");
        setWidgetSizeScale(s.widget_size_scale ?? 100);
        setPrivacyEnabled(!!s.privacy_enabled);
        if (s.privacy_config) {
          try {
            const parsed = typeof s.privacy_config === 'string' ? JSON.parse(s.privacy_config) : s.privacy_config;
            setPrivacyConfig(prev => ({ ...prev, ...parsed }));
          } catch (e) {}
        }
        setCustomStatementUrl(s.custom_statement_url || "");

        // Fetch privacy stats
        api.get(`/api/wordpress/privacy/${s.site_key}/stats`)
          .then(({ data }) => setPrivacyStats(data.stats))
          .catch(() => {});
      })
      .catch((err) => setError(err?.response?.data?.message || err?.message || "שגיאה"));
  }, [id]);

  const uploadLogo = async (file) => {
    if (!file) return;
    setError("");
    setDone(false);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const { data } = await api.post("/api/uploads/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) setBrandLogoUrl(String(data.url));
    } catch (err) {
      setError(err?.response?.data?.message || "שגיאה בהעלאת קובץ");
    } finally {
      setUploading(false);
    }
  };

  const uploadIcon = async (file) => {
    if (!file) return;
    setError("");
    setDone(false);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const { data } = await api.post("/api/uploads/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) {
        setWidgetIconUrl(toAbsoluteUrl(data.url));
        setWidgetIconStyle("custom");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "שגיאה בהעלאת קובץ");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setDone(false);
    setError("");
    try {
      await api.put(`/api/sites/${id}`, {
        name,
        domain,
        status,
        brandName,
        brandLogoUrl,
        brandMarketingText,
        widgetFontFamily,
        widgetPosition,
        widgetMarginTop: Number(widgetMarginTop),
        widgetMarginBottom: Number(widgetMarginBottom),
        widgetIconStyle,
        widgetIconUrl: widgetIconUrl.trim(),
        widgetIconColor,
        widgetMarginSide: Number(widgetMarginSide),
        widgetButtonSize: Number(widgetButtonSize),
        widgetButtonShape,
        widgetLanguage,
        cookieButtonMode,
        widgetShortcut,
        widgetAllowHide,
        widgetButtonColor,
        widgetButtonHoverColor,
        widgetShowCloseButton,
        widgetCustomTitle,
        widgetCustomTagline,
        widgetSizeScale: Number(widgetSizeScale),
        privacyEnabled,
        privacyConfig,
        customStatementUrl,
        privacyMarginTop: Number(privacyMarginTop),
        privacyMarginBottom: Number(privacyMarginBottom),
      });
      setDone(true);
      setTimeout(() => nav("/sites"), 500);
    } catch (err) {
      setError(err?.response?.data?.message || "שגיאה");
    }
  };

  const tab = TABS.some((x) => x.value === searchParams.get("tab")) ? searchParams.get("tab") : "general";
  const setTab = (v) => setSearchParams(v === "general" ? {} : { tab: v }, { replace: true });
  const isRtl = theme.direction === "rtl";
  const BackIcon = isRtl ? ArrowForwardRoundedIcon : ArrowBackRoundedIcon;
  // stylis-plugin-rtl flips left/right in RTL; widget positions are physical screen sides, so pre-flip them.
  const phys = (side, val) => ({ [isRtl ? (side === "left" ? "right" : "left") : side]: val });
  const shapeRadius = (big) =>
    widgetButtonShape === "circle" ? "50%" : widgetButtonShape === "rounded" ? (big ? "16px" : "8px") : big ? "6px" : "3px";
  const pc = privacyConfig;
  const setPc = (patch) => setPrivacyConfig({ ...privacyConfig, ...patch });

  // ---------- Live preview (browser mock with the floating button) ----------
  const btnSize = Number(widgetButtonSize) || 52;
  const [pv, ph] = String(widgetPosition || "bottom-right").split("-");
  const scale = 0.62;
  const previewCard = (
    <SectionCard title="תצוגה מקדימה" description="כך ייראה הכפתור באתר" icon={<WebAssetOutlinedIcon />} contentSx={{ pt: 1.5 }}>
      <Box
        aria-hidden="true"
        sx={{
          borderRadius: 1.5,
          border: 1,
          borderColor: "divider",
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ px: 1.25, height: 26, bgcolor: (t) => t.aw.soft, borderBottom: 1, borderColor: "divider" }}>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: (t) => t.aw.borderStrong }} />
          ))}
          <Box sx={{ flex: 1, mx: 1, height: 12, borderRadius: 99, bgcolor: "background.paper" }} />
        </Stack>
        <Box sx={{ position: "relative", height: 210, p: 1.75 }}>
          <Box sx={{ height: 10, width: "45%", borderRadius: 99, bgcolor: (t) => t.aw.soft, mb: 1.25 }} />
          <Box sx={{ height: 7, width: "85%", borderRadius: 99, bgcolor: (t) => t.aw.soft, mb: 0.75 }} />
          <Box sx={{ height: 7, width: "75%", borderRadius: 99, bgcolor: (t) => t.aw.soft, mb: 0.75 }} />
          <Box sx={{ height: 7, width: "60%", borderRadius: 99, bgcolor: (t) => t.aw.soft, mb: 2 }} />
          <Box sx={{ height: 54, width: "100%", borderRadius: 1, bgcolor: (t) => t.aw.softer, border: 1, borderColor: "divider" }} />
          <Box
            sx={{
              position: "absolute",
              width: btnSize * scale,
              height: btnSize * scale,
              borderRadius: shapeRadius(true),
              bgcolor: widgetButtonColor,
              color: widgetIconColor,
              display: "grid",
              placeItems: "center",
              boxShadow: "0 6px 16px rgba(15,23,42,0.25)",
              transition: "all .2s",
              "&:hover": { bgcolor: widgetButtonHoverColor },
              ...(pv === "top"
                ? { top: Math.min(40, Number(widgetMarginTop) * scale || 0) + 6 }
                : pv === "middle"
                ? { top: `calc(50% - ${(btnSize * scale) / 2}px)` }
                : { bottom: Math.min(40, Number(widgetMarginBottom) * scale || 0) + 6 }),
              ...(ph === "center"
                ? phys("left", `calc(50% - ${(btnSize * scale) / 2}px)`)
                : phys(ph === "left" ? "left" : "right", Math.min(40, Number(widgetMarginSide) * scale || 0) + 6)),
            }}
          >
            <WidgetIcon style={widgetIconStyle} iconUrl={widgetIconUrl} size={Math.round(btnSize * scale * 0.56)} />
          </Box>
        </Box>
      </Box>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
        <Pill tone="neutral">{(WIDGET_POSITION_OPTIONS.find((o) => o.value === widgetPosition) || {}).label || widgetPosition}</Pill>
        <Pill tone="neutral">{btnSize}px</Pill>
        <Pill tone="neutral">{widgetButtonShape === "circle" ? "עיגול" : widgetButtonShape === "rounded" ? "ריבוע מעוגל" : "ריבוע"}</Pill>
      </Stack>
    </SectionCard>
  );

  // ---------- Tab panels ----------
  const generalPanel = (
    <Stack spacing={2.5}>
      <SectionCard title="פרטי האתר" description="שם, דומיינים מורשים וסטטוס הפעלה" icon={<LanguageOutlinedIcon />}>
        <Stack spacing={2.5}>
          <TextField label="שם האתר" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          <TextField
            label="דומיין (אפשר כמה מופרדים בפסיק)"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            helperText="הווידג׳ט ייטען רק בדומיינים שברשימה"
            fullWidth
          />
          <TextField select label="סטטוס" value={status} onChange={(e) => setStatus(e.target.value)} fullWidth>
            <MenuItem value="active">פעיל (active)</MenuItem>
            <MenuItem value="disabled">מושבת (disabled)</MenuItem>
          </TextField>
        </Stack>
      </SectionCard>
      {site ? (
        <SectionCard title="פעולות מהירות" icon={<TuneRoundedIcon />} iconTone="neutral">
          <Box sx={grid3}>
            {[
              { to: `/sites/${id}/install`, label: "קוד התקנה", icon: <CodeIcon /> },
              { to: `/sites/${id}/compliance`, label: "דוח תאימות", icon: <AssessmentOutlinedIcon /> },
              { to: `/sites/${id}/privacy-policy`, label: "מדיניות פרטיות", icon: <PolicyOutlinedIcon /> },
            ].map((a) => (
              <Button key={a.to} component={RouterLink} to={a.to} variant="outlined" startIcon={a.icon} sx={{ justifyContent: "flex-start" }}>
                {a.label}
              </Button>
            ))}
          </Box>
        </SectionCard>
      ) : null}
    </Stack>
  );

  const designPanel = (
    <Stack spacing={2.5}>
      {isNarrow ? previewCard : null}

      <SectionCard
        title="כפתור הנגישות הצף"
        description="מיקום, מרווחים, אייקון וצבעים — כדי להתאים את הכפתור לעיצוב האתר"
        icon={<PlaceOutlinedIcon />}
      >
        <SubHeading icon={<PlaceOutlinedIcon />}>מיקום</SubHeading>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "232px 1fr" }, gap: 2.5, alignItems: "start" }}>
          {/* Visual position picker: a mini "screen" with 8 anchor points */}
          <Box
            role="radiogroup"
            aria-label="מיקום הכפתור"
            sx={{
              position: "relative",
              width: 232,
              height: 148,
              border: 1,
              borderColor: (t) => t.aw.borderStrong,
              borderRadius: 2,
              bgcolor: (t) => t.aw.soft,
              backgroundImage: (t) =>
                `linear-gradient(${t.aw.border} 1px, transparent 1px), linear-gradient(90deg, ${t.aw.border} 1px, transparent 1px)`,
              backgroundSize: "24px 24px",
            }}
          >
            {WIDGET_POSITION_OPTIONS.map((o) => {
              const [v, h] = o.value.split("-");
              const selected = widgetPosition === o.value;
              return (
                <Box
                  key={o.value}
                  component="button"
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={o.label}
                  title={o.label}
                  onClick={() => setWidgetPosition(o.value)}
                  sx={{
                    position: "absolute",
                    width: 28,
                    height: 28,
                    p: 0,
                    borderRadius: shapeRadius(false),
                    border: "2px solid",
                    borderColor: selected ? widgetButtonColor : (t) => t.aw.borderStrong,
                    bgcolor: selected ? widgetButtonColor : "background.paper",
                    color: selected ? widgetIconColor : "text.secondary",
                    boxShadow: selected ? "0 4px 10px rgba(15,23,42,0.2)" : "none",
                    cursor: "pointer",
                    display: "grid",
                    placeItems: "center",
                    transition: "transform .15s",
                    "&:hover": { transform: "scale(1.08)", borderColor: selected ? widgetButtonColor : "primary.main" },
                    ...(v === "top" ? { top: 8 } : v === "middle" ? { top: "calc(50% - 14px)" } : { bottom: 8 }),
                    ...(h === "left" ? phys("left", 8) : h === "center" ? phys("left", "calc(50% - 14px)") : phys("right", 8)),
                    "&:focus-visible": { outline: "3px solid", outlineColor: "primary.main", outlineOffset: 2 },
                  }}
                >
                  {selected ? <WidgetIcon style={widgetIconStyle} iconUrl={widgetIconUrl} size={14} /> : null}
                </Box>
              );
            })}
          </Box>
          <TextField
            select
            label="מיקום הכפתור"
            value={widgetPosition}
            onChange={(e) => setWidgetPosition(e.target.value)}
            helperText="אפשר לבחור גם בלחיצה על המסך הקטן"
            fullWidth
          >
            {WIDGET_POSITION_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ ...grid3, mt: 2.5 }}>
          <TextField
            type="number"
            label="מרווח מהצד"
            value={widgetMarginSide}
            onChange={(e) => setWidgetMarginSide(e.target.value)}
            inputProps={{ min: 0, max: 500 }}
            InputProps={pxAdornment}
            helperText="מרחק מהקצה הימני/שמאלי"
          />
          <TextField
            type="number"
            label="מרווח מלמעלה"
            value={widgetMarginTop}
            onChange={(e) => setWidgetMarginTop(e.target.value)}
            inputProps={{ min: 0, max: 500 }}
            InputProps={pxAdornment}
            helperText="למיקומים עליונים"
          />
          <TextField
            type="number"
            label="מרווח מלמטה"
            value={widgetMarginBottom}
            onChange={(e) => setWidgetMarginBottom(e.target.value)}
            inputProps={{ min: 0, max: 500 }}
            InputProps={pxAdornment}
            helperText="למיקומים תחתונים"
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <SubHeading icon={<EmojiSymbolsOutlinedIcon />}>אייקון</SubHeading>
        <Box
          role="radiogroup"
          aria-label="אייקון הכפתור"
          sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(60px, 1fr))", gap: 1 }}
        >
          {WIDGET_ICON_OPTIONS.filter((o) => o.value !== "custom").map((o) => {
            const selected = widgetIconStyle === o.value;
            return (
              <Box
                key={o.value}
                component="button"
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={o.label}
                title={o.label}
                onClick={() => setWidgetIconStyle(o.value)}
                sx={{
                  height: 60,
                  p: 0,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: selected ? "primary.main" : "divider",
                  boxShadow: (t) => (selected ? `0 0 0 2px ${t.palette.primary.main}` : "none"),
                  bgcolor: selected ? widgetButtonColor : "background.paper",
                  color: selected ? widgetIconColor : "text.primary",
                  cursor: "pointer",
                  display: "grid",
                  placeItems: "center",
                  transition: "border-color .15s, background-color .15s",
                  "&:hover": { borderColor: "primary.main", bgcolor: selected ? widgetButtonColor : (t) => t.aw.softer },
                  "&:focus-visible": { outline: "3px solid", outlineColor: "primary.main", outlineOffset: 2 },
                }}
              >
                <WidgetIcon style={o.value} size={26} />
              </Box>
            );
          })}
        </Box>
        <Typography variant="caption" component="div" sx={{ mt: 1 }}>
          נבחר: {(WIDGET_ICON_OPTIONS.find((o) => o.value === widgetIconStyle) || {}).label || ""}
        </Typography>

        <Box sx={{ mt: 2.5, p: 2, borderRadius: 1.5, border: 1, borderColor: "divider", bgcolor: (t) => t.aw.softer }}>
          <Typography variant="subtitle2" component="h4" sx={{ color: "text.primary", mb: 1.5 }}>
            אייקון מותאם אישית
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
            <input
              ref={iconFileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              style={{ display: "none" }}
              onChange={(e) => uploadIcon(e.target.files && e.target.files[0])}
            />
            <Button variant="outlined" startIcon={<UploadRoundedIcon />} onClick={() => iconFileRef.current?.click()} disabled={uploading}>
              {uploading ? "מעלה..." : "העלה אייקון"}
            </Button>
            {widgetIconUrl ? (
              <>
                <Button
                  variant={widgetIconStyle === "custom" ? "contained" : "text"}
                  onClick={() => setWidgetIconStyle("custom")}
                  disabled={uploading}
                >
                  השתמש באייקון שהועלה
                </Button>
                <Button
                  variant="text"
                  color="error"
                  onClick={() => {
                    setWidgetIconUrl("");
                    if (widgetIconStyle === "custom") setWidgetIconStyle("access-circle");
                  }}
                  disabled={uploading}
                >
                  הסר
                </Button>
              </>
            ) : null}
          </Stack>
          <TextField
            label="או קישור לאייקון (URL)"
            value={widgetIconUrl}
            onChange={(e) => setWidgetIconUrl(e.target.value)}
            placeholder="https://example.com/icon.png"
            helperText="מומלץ PNG/WebP שקוף, ריבועי, עד 2MB."
            size="small"
            fullWidth
            inputProps={{ dir: "ltr" }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        <SubHeading icon={<FormatPaintOutlinedIcon />}>גודל, צורה וצבעים</SubHeading>
        <Box sx={grid2}>
          <TextField select label="גודל הכפתור" value={String(widgetButtonSize)} onChange={(e) => setWidgetButtonSize(Number(e.target.value))}>
            <MenuItem value="44">קטן (44px)</MenuItem>
            <MenuItem value="52">בינוני (52px)</MenuItem>
            <MenuItem value="60">גדול (60px)</MenuItem>
            <MenuItem value="72">גדול מאוד (72px)</MenuItem>
            {![44, 52, 60, 72].includes(Number(widgetButtonSize)) ? (
              <MenuItem value={String(widgetButtonSize)}>{widgetButtonSize}px</MenuItem>
            ) : null}
          </TextField>
          <Box>
            <Typography variant="caption" component="div" id="shape-label" sx={{ mb: 0.75 }}>
              צורת הכפתור
            </Typography>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={widgetButtonShape}
              onChange={(_, v) => v && setWidgetButtonShape(v)}
              aria-labelledby="shape-label"
              sx={{
                bgcolor: (t) => t.aw.soft,
                p: 0.5,
                borderRadius: 1.5,
                gap: 0.5,
                "& .MuiToggleButton-root": {
                  border: 0,
                  borderRadius: "9px !important",
                  fontWeight: 600,
                  color: "text.secondary",
                  py: 0.9,
                  "&.Mui-selected": { bgcolor: "background.paper", color: "primary.main", boxShadow: (t) => t.aw.shadowSm },
                  "&.Mui-selected:hover": { bgcolor: "background.paper" },
                },
              }}
            >
              <ToggleButton value="circle">עיגול</ToggleButton>
              <ToggleButton value="rounded">ריבוע מעוגל</ToggleButton>
              <ToggleButton value="square">ריבוע</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        <Box sx={{ ...grid3, mt: 2 }}>
          <ColorField label="צבע כפתור" value={widgetButtonColor} onChange={setWidgetButtonColor} placeholder="#1976d2" />
          <ColorField label="צבע hover" value={widgetButtonHoverColor} onChange={setWidgetButtonHoverColor} placeholder="#1565c0" />
          <ColorField label="צבע אייקון" value={widgetIconColor} onChange={setWidgetIconColor} placeholder="#ffffff" />
        </Box>
      </SectionCard>

      <SectionCard title="ממשק התפריט" description="הטקסטים והנראות של החלון שנפתח באתר" icon={<WebAssetOutlinedIcon />} iconTone="violet">
        <Box sx={grid2}>
          <TextField
            label="כותרת תפריט"
            value={widgetCustomTitle}
            onChange={(e) => setWidgetCustomTitle(e.target.value)}
            placeholder="Accss"
            helperText="ברירת מחדל: Accss"
          />
          <TextField
            label="תת-כותרת (Tagline)"
            value={widgetCustomTagline}
            onChange={(e) => setWidgetCustomTagline(e.target.value)}
            placeholder="פתרון נגישות"
          />
          <TextField
            type="number"
            label="קנה מידה (Size %)"
            value={widgetSizeScale}
            onChange={(e) => setWidgetSizeScale(e.target.value)}
            inputProps={{ min: 50, max: 150 }}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            helperText="הגדלה/הקטנה של התפריט (50-150%)"
          />
          <TextField
            select
            label="פונט לתפריט הווידג׳ט"
            value={widgetFontFamily}
            onChange={(e) => setWidgetFontFamily(e.target.value)}
            helperText="מומלץ להשאיר “פונט מערכת” כדי להבטיח תצוגה תקינה בכל אתר."
          >
            <MenuItem value="">פונט מערכת (מומלץ)</MenuItem>
            <MenuItem value="system-ui,-apple-system,Segoe UI,Arial,sans-serif">System UI</MenuItem>
            <MenuItem value="Arial,sans-serif">Arial</MenuItem>
            <MenuItem value="'Segoe UI',Arial,sans-serif">Segoe UI</MenuItem>
            <MenuItem value="Tahoma,Arial,sans-serif">Tahoma</MenuItem>
            <MenuItem value="Verdana,Arial,sans-serif">Verdana</MenuItem>
          </TextField>
        </Box>
        <Divider sx={{ mt: 2 }} />
        <SwitchRow
          label="הצג כפתור סגירה (X)"
          description="כפתור סגירה בפינת חלון הנגישות"
          checked={widgetShowCloseButton}
          onChange={setWidgetShowCloseButton}
        />
      </SectionCard>

      <SectionCard
        title="White Label / מיתוג בווידג׳ט"
        description="יופיע בווידג׳ט רק אם החבילה שלך כוללת White Label. אחרת יוצג “Accss”."
        icon={<BrandingWatermarkOutlinedIcon />}
        iconTone="teal"
      >
        <Box
          sx={{ p: 2, mb: 2.5, borderRadius: 1.5, border: 1, borderColor: "divider", bgcolor: (t) => t.aw.softer }}
          aria-label="תצוגה מקדימה של המיתוג"
          role="group"
        >
          <Typography variant="overline" component="div" sx={{ mb: 1 }}>
            תצוגה מקדימה
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                border: 1,
                borderColor: "divider",
                bgcolor: "background.paper",
                display: "grid",
                placeItems: "center",
                overflow: "hidden",
                flex: "none",
              }}
            >
              {preview.logoUrl ? (
                <img src={preview.logoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
              ) : (
                <Typography sx={{ fontWeight: 900, fontSize: 12 }}>AW</Typography>
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>
                {preview.name}
              </Typography>
              {preview.marketingText ? (
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
                  {preview.marketingText}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </Box>
        <Stack spacing={2.5}>
          <TextField
            label="שם מותג להצגה בווידג׳ט"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            placeholder="לדוגמה: OwnAvatar"
          />
          <Box>
            <TextField
              label="קישור ללוגו (URL) או העלאה"
              value={brandLogoUrl}
              onChange={(e) => setBrandLogoUrl(e.target.value)}
              placeholder="https://example.com/logo.png"
              fullWidth
              inputProps={{ dir: "ltr" }}
              helperText="מומלץ PNG/WebP שקוף, עד 2MB."
            />
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                style={{ display: "none" }}
                onChange={(e) => uploadLogo(e.target.files && e.target.files[0])}
              />
              <Button variant="outlined" size="small" startIcon={<UploadRoundedIcon />} onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? "מעלה..." : "העלה לוגו"}
              </Button>
              {brandLogoUrl ? (
                <Button variant="text" size="small" onClick={() => setBrandLogoUrl("")} disabled={uploading}>
                  נקה לוגו
                </Button>
              ) : null}
            </Stack>
          </Box>
          <TextField
            label="טקסט שיווקי קצר (אופציונלי)"
            value={brandMarketingText}
            onChange={(e) => setBrandMarketingText(e.target.value)}
            placeholder="לדוגמה: פתרון נגישות מתקדם לאתר"
            helperText="עדיף משפט קצר כדי לשמור על תפריט קומפקטי."
          />
        </Stack>
      </SectionCard>
    </Stack>
  );

  const behaviorPanel = (
    <SectionCard title="התנהגות הווידג׳ט" description="שפה, כפתור העוגיות וקיצורי מקלדת" icon={<TouchAppOutlinedIcon />}>
      <Box sx={grid2}>
        <TextField
          select
          label="שפת הווידג׳ט"
          value={widgetLanguage}
          onChange={(e) => setWidgetLanguage(e.target.value)}
          helperText="אוטומטי = לפי שפת האתר. הגולש יכול להחליף שפה מתוך הווידג׳ט."
        >
          <MenuItem value="auto">אוטומטי (לפי שפת האתר)</MenuItem>
          <MenuItem value="he">עברית</MenuItem>
          <MenuItem value="en">English</MenuItem>
          <MenuItem value="ar">العربية</MenuItem>
          <MenuItem value="ru">Русский</MenuItem>
        </TextField>
        <TextField
          select
          label="כפתור העדפות עוגיות"
          value={cookieButtonMode}
          onChange={(e) => setCookieButtonMode(e.target.value)}
          helperText={privacyEnabled ? "איך הגולשים פותחים את העדפות העוגיות אחרי שבחרו" : "רלוונטי כשבאנר הפרטיות פעיל"}
        >
          <MenuItem value="separate">כפתור צף נפרד, ליד כפתור הנגישות</MenuItem>
          <MenuItem value="panel">בתוך חלון הנגישות</MenuItem>
          <MenuItem value="none">בלי כפתור (רק קישור בתחתית חלון הנגישות)</MenuItem>
        </TextField>
      </Box>
      <Divider sx={{ mt: 2.5 }} />
      <SwitchRow
        label="קיצור מקלדת Alt + A"
        description="פתיחה וסגירה של חלון הנגישות מהמקלדת"
        checked={widgetShortcut}
        onChange={setWidgetShortcut}
      />
      <Divider />
      <SwitchRow
        label="אפשרות ״הסתרה״ לגולשים"
        description="הגולש יכול להסתיר את הכפתור עד סוף הביקור"
        checked={widgetAllowHide}
        onChange={setWidgetAllowHide}
      />
    </SectionCard>
  );

  const privacyPanel = (
    <Stack spacing={2.5}>
      <SectionCard
        title="שירותי פרטיות (תיקון 13)"
        description="באנר הסכמת עוגיות (Cookie Consent) מותאם לחוק הגנת הפרטיות הישראלי"
        icon={<CookieOutlinedIcon />}
        iconTone="teal"
      >
        <SwitchRow
          label="הפעל באנר פרטיות"
          description={privacyEnabled ? "פעיל (מומלץ)" : "כבוי — הבאנר לא יוצג באתר"}
          checked={privacyEnabled}
          onChange={setPrivacyEnabled}
        />
        {privacyEnabled && privacyStats ? (
          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <InsightsOutlinedIcon sx={{ fontSize: 18, color: (t) => t.aw.faint }} />
              <Typography variant="subtitle2" component="h3">
                סטטיסטיקה (30 יום אחרונים)
              </Typography>
            </Stack>
            <Grid container spacing={1.5}>
              {[
                { label: "צפיות", value: privacyStats.totals.total_views || 0, color: "text.primary" },
                { label: "אישור הכל", value: privacyStats.totals.total_accepts || 0, color: "success.main" },
                { label: "דחייה", value: privacyStats.totals.total_rejects || 0, color: "error.main" },
                { label: "מותאם", value: privacyStats.totals.total_custom || 0, color: "info.main" },
              ].map((s) => (
                <Grid item xs={6} sm={3} key={s.label}>
                  <Box sx={{ p: 1.5, borderRadius: 1.5, border: 1, borderColor: "divider", bgcolor: (t) => t.aw.softer }}>
                    <Typography variant="caption" component="div">
                      {s.label}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: "1.35rem", color: s.color }}>{Number(s.value).toLocaleString()}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : null}
      </SectionCard>

      {privacyEnabled && (
        <>
          <SectionCard title="הגדרות באנר הסכמה" description="מיקום, ריווח וטקסטים" icon={<WebAssetOutlinedIcon />}>
            <Stack spacing={2.5}>
              <Box sx={grid3}>
                <TextField select label="מיקום הבאנר" value={pc.position || "left"} onChange={(e) => setPc({ position: e.target.value })}>
                  <MenuItem value="left">פינה שמאלית</MenuItem>
                  <MenuItem value="right">פינה ימנית</MenuItem>
                  <MenuItem value="bottom">פס תחתון מלא</MenuItem>
                </TextField>
                <TextField
                  type="number"
                  label="ריווח באנר מלמעלה"
                  value={privacyMarginTop}
                  onChange={(e) => setPrivacyMarginTop(e.target.value)}
                  inputProps={{ min: 0, max: 500 }}
                  InputProps={pxAdornment}
                  helperText="מרחק מהקצה העליון של המסך"
                />
                <TextField
                  type="number"
                  label="ריווח באנר מלמטה"
                  value={privacyMarginBottom}
                  onChange={(e) => setPrivacyMarginBottom(e.target.value)}
                  inputProps={{ min: 0, max: 500 }}
                  InputProps={pxAdornment}
                  helperText="מרחק מהקצה התחתון של המסך"
                />
              </Box>
              <Box sx={grid2}>
                <TextField
                  label="כותרת הבאנר"
                  value={pc.bannerTitle}
                  onChange={(e) => setPc({ bannerTitle: e.target.value })}
                  placeholder="הפרטיות שלכם חשובה לנו"
                />
                <TextField
                  label="טקסט אישור הכל"
                  value={pc.acceptAllText}
                  onChange={(e) => setPc({ acceptAllText: e.target.value })}
                  placeholder="אישור הכל"
                />
              </Box>
              <TextField
                label="תוכן הבאנר"
                multiline
                minRows={2}
                value={pc.bannerText}
                onChange={(e) => setPc({ bannerText: e.target.value })}
                placeholder="אנו משתמשים בעוגיות לשיפור החוויה..."
              />
              <Box sx={grid3}>
                <TextField label="טקסט דחייה" value={pc.rejectText} onChange={(e) => setPc({ rejectText: e.target.value })} placeholder="דחייה" />
                <TextField
                  label="טקסט העדפות"
                  value={pc.preferencesText}
                  onChange={(e) => setPc({ preferencesText: e.target.value })}
                  placeholder="העדפות"
                />
                <TextField
                  label="כותרת חלון העדפות"
                  value={pc.modalTitle}
                  onChange={(e) => setPc({ modalTitle: e.target.value })}
                  placeholder="הגדרות פרטיות"
                />
              </Box>
            </Stack>
          </SectionCard>

          <SectionCard title="מדיניות פרטיות" description="קישור ופרטים שיוצגו במדיניות" icon={<PolicyOutlinedIcon />} iconTone="violet">
            <Stack spacing={2.5}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" }, gap: 2 }}>
                <TextField
                  label="קישור לעמוד מדיניות פרטיות"
                  value={pc.policyUrl}
                  onChange={(e) => setPc({ policyUrl: e.target.value })}
                  placeholder="https://example.com/privacy-policy"
                  inputProps={{ dir: "ltr" }}
                />
                <TextField label="טקסט לקישור" value={pc.policyText} onChange={(e) => setPc({ policyText: e.target.value })} placeholder="מדיניות הפרטיות" />
              </Box>
              <Box sx={grid3}>
                <TextField
                  label="שם חברה (למדיניות)"
                  value={pc.policyCompanyName || ""}
                  onChange={(e) => setPc({ policyCompanyName: e.target.value })}
                  placeholder="שם החברה כפי שיוצג במדיניות"
                />
                <TextField
                  label="אימייל (למדיניות)"
                  value={pc.policyEmail || ""}
                  onChange={(e) => setPc({ policyEmail: e.target.value })}
                  placeholder="info@example.com"
                  inputProps={{ dir: "ltr" }}
                />
                <TextField
                  label="אתר (למדיניות)"
                  value={pc.policyWebsite || ""}
                  onChange={(e) => setPc({ policyWebsite: e.target.value })}
                  placeholder="https://example.com"
                  inputProps={{ dir: "ltr" }}
                />
              </Box>
            </Stack>
          </SectionCard>

          <SectionCard title="עיצוב באנר ותזכורת" description="צבעים, פינות וכפתור התזכורת" icon={<FormatPaintOutlinedIcon />} iconTone="warning">
            <SubHeading>עיצוב באנר</SubHeading>
            <Box sx={{ ...grid2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" } }}>
              <ColorField label="רקע באנר" value={pc.bannerBg} onChange={(v) => setPc({ bannerBg: v })} />
              <ColorField label="טקסט באנר" value={pc.bannerTextColor} onChange={(v) => setPc({ bannerTextColor: v })} />
              <ColorField label="צבע קישורים" value={pc.linkColor} onChange={(v) => setPc({ linkColor: v })} />
              <ColorField label="כפתור ראשי" value={pc.primaryBtnBg} onChange={(v) => setPc({ primaryBtnBg: v })} />
              <ColorField label="טקסט כפתור ראשי" value={pc.primaryBtnTextColor} onChange={(v) => setPc({ primaryBtnTextColor: v })} />
              <TextField
                type="number"
                label="עיגול פינות"
                value={pc.bannerRadius}
                onChange={(e) => setPc({ bannerRadius: Number(e.target.value || 12) })}
                InputProps={pxAdornment}
              />
              <ColorField label="כפתור משני" value={pc.secondaryBtnBg} onChange={(v) => setPc({ secondaryBtnBg: v })} />
              <ColorField label="טקסט כפתור משני" value={pc.secondaryBtnTextColor} onChange={(v) => setPc({ secondaryBtnTextColor: v })} />
            </Box>

            <Divider sx={{ my: 3 }} />
            <SubHeading icon={<NotificationsActiveOutlinedIcon />}>תזכורת</SubHeading>
            <SwitchRow
              label="הצג תזכורת אחרי סגירה"
              description="כפתור קטן שמאפשר לגולש לשנות את בחירתו"
              checked={pc.showReminder !== false}
              onChange={(v) => setPc({ showReminder: v })}
            />
            <Box sx={{ ...grid3, mt: 1.5 }}>
              <TextField select label="מיקום תזכורת" value={pc.reminderPosition || "right"} onChange={(e) => setPc({ reminderPosition: e.target.value })}>
                <MenuItem value="right">ימין</MenuItem>
                <MenuItem value="left">שמאל</MenuItem>
              </TextField>
              <ColorField label="רקע תזכורת" value={pc.reminderBg} onChange={(v) => setPc({ reminderBg: v })} />
              <ColorField label="טקסט תזכורת" value={pc.reminderTextColor} onChange={(v) => setPc({ reminderTextColor: v })} />
            </Box>
          </SectionCard>
        </>
      )}
    </Stack>
  );

  const statementPanel = (
    <SectionCard
      title="הצהרת נגישות"
      description="כברירת מחדל, הווידג׳ט מקשר להצהרת נגישות שנוצרת מהמערכת"
      icon={<DescriptionOutlinedIcon />}
      action={
        <Button component={RouterLink} to={`/sites/${id}/statement`} size="small" variant="outlined">
          עריכת ההצהרה המובנית
        </Button>
      }
    >
      <TextField
        label="קישור מותאם אישית להצהרת נגישות (אופציונלי)"
        value={customStatementUrl}
        onChange={(e) => setCustomStatementUrl(e.target.value)}
        placeholder="https://example.com/accessibility-statement"
        helperText="אם יש לך עמוד הצהרת נגישות משלך באתר, הזן כאן את ה-URL המלא. אם ריק, הווידג'ט יקשר להצהרה המובנית של המערכת."
        type="url"
        fullWidth
        inputProps={{ dir: "ltr" }}
      />
    </SectionCard>
  );

  const panels = { general: generalPanel, design: designPanel, behavior: behaviorPanel, privacy: privacyPanel, statement: statementPanel };

  return (
    <Box>
      <Button
        component={RouterLink}
        to="/sites"
        size="small"
        startIcon={<BackIcon />}
        sx={{ mb: 1.5, color: "text.secondary", minHeight: 30, px: 1 }}
      >
        כל האתרים
      </Button>
      <PageHeader
        title={site ? site.name || "עריכת אתר" : "עריכת אתר"}
        subtitle={
          site ? (
            <Stack direction="row" spacing={1} alignItems="center" component="span" useFlexGap flexWrap="wrap">
              <Pill tone={status === "active" ? "success" : "neutral"}>
                <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "currentColor" }} />
                {status === "active" ? "פעיל" : "מושבת"}
              </Pill>
              <Box component="span">{site.domain}</Box>
            </Stack>
          ) : (
            "הגדרות האתר, הווידג׳ט ובאנר הפרטיות"
          )
        }
        actions={
          site ? (
            <>
              <Button component={RouterLink} to={`/sites/${id}/install`} variant="outlined" startIcon={<CodeIcon />}>
                קוד התקנה
              </Button>
              <Button type="submit" form="site-edit-form" variant="contained" startIcon={<SaveRoundedIcon />} sx={{ display: { xs: "none", sm: "inline-flex" } }}>
                שמירה
              </Button>
            </>
          ) : null
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      ) : null}

      {!site ? (
        !error ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={44} sx={{ maxWidth: 640 }} />
            <Skeleton variant="rounded" height={260} />
          </Stack>
        ) : null
      ) : (
        <Box component="form" id="site-edit-form" onSubmit={save} noValidate={false}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons={false}
            aria-label="קטגוריות הגדרה"
            sx={{ mb: 2.5, display: "inline-flex", maxWidth: "100%" }}
          >
            {TABS.map((tb) => (
              <Tab
                key={tb.value}
                value={tb.value}
                label={tb.label}
                icon={tb.icon}
                iconPosition="start"
                id={`site-tab-${tb.value}`}
                aria-controls={`site-tabpanel-${tb.value}`}
                sx={{ minHeight: 36, "& .MuiTab-iconWrapper": { fontSize: 18 } }}
              />
            ))}
          </Tabs>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: tab === "design" ? "minmax(0,1fr) 320px" : "1fr" }, gap: 2.5, alignItems: "start" }}>
            <Box role="tabpanel" id={`site-tabpanel-${tab}`} aria-labelledby={`site-tab-${tab}`} sx={{ minWidth: 0 }}>
              {panels[tab]}
            </Box>
            {tab === "design" && !isNarrow ? <Box sx={{ position: "sticky", top: 88 }}>{previewCard}</Box> : null}
          </Box>

          {/* Sticky save bar */}
          <Card
            sx={{
              position: "sticky",
              bottom: 16,
              mt: 3,
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              zIndex: 2,
              boxShadow: (t) => t.aw.shadowMd,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }} aria-live="polite">
              {done ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "success.main" }}>
                  <CheckCircleRoundedIcon fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    נשמר.
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ color: "text.secondary", display: { xs: "none", sm: "block" } }}>
                  השינויים יחולו על האתר לאחר השמירה
                </Typography>
              )}
            </Box>
            <Button component={RouterLink} to="/sites" sx={{ color: "text.secondary" }}>
              ביטול
            </Button>
            <Button type="submit" variant="contained" startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}>
              שמירה
            </Button>
          </Card>
        </Box>
      )}
    </Box>
  );
}
